import { readonly, ref, type Ref } from 'vue'

/**
 * Tracks whether the user is currently driving the page with the keyboard.
 *
 * Browsers expose no screen-reader signal on purpose (it would be a
 * fingerprinting vector), so this is the closest reliable proxy we can act on:
 * a status message triggered by a key press must stay on screen long enough to
 * be reached with Tab, while one triggered by a click can fade on its own.
 * Same heuristic the `:focus-visible` pseudo-class is built on.
 */

const isKeyboardModality = ref(false)
let listening = false

function handleKeyDown(event: KeyboardEvent): void {
  // Browser and OS chords (Cmd+R, Alt+Tab...) are not player interaction.
  if (event.metaKey || event.ctrlKey || event.altKey) return
  isKeyboardModality.value = true
}

function handlePointerDown(): void {
  isKeyboardModality.value = false
}

/**
 * Listeners are attached once for the lifetime of the document: they are two
 * capture-phase handlers doing a boolean assignment, and every player instance
 * shares the same signal.
 */
function startListening(): void {
  if (listening || typeof document === 'undefined') return
  listening = true
  document.addEventListener('keydown', handleKeyDown, { capture: true, passive: true })
  document.addEventListener('pointerdown', handlePointerDown, { capture: true, passive: true })
}

export interface UseInputModalityReturn {
  /** True when the last meaningful interaction came from the keyboard. */
  isKeyboardModality: Readonly<Ref<boolean>>
}

export function useInputModality(): UseInputModalityReturn {
  startListening()
  return { isKeyboardModality: readonly(isKeyboardModality) }
}
